import type { FC, ReactNode } from 'react';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  BlocksCLIServer,
  HttpServer,
} from '@wix/cli-blocks-editor-comm-api';
import { createBlocksCLIServer } from '@wix/cli-blocks-editor-comm-api';
import { useErrorReporter, ErrorViewer } from '@wix/cli-error-reporting';
import { useTranslation } from '@wix/cli-i18n';
import { useProjectModel } from '../../model.js';
import { useDevEnvironment } from './DevEnvironment.js';
import { saveWixConfig } from '../../files/wix.config.js';
import { useBlocksComponentsRef } from './blocks-cli-dev-server/useSyncBlocksComponents.js';
import syncBlocksComponents from './blocks-cli-dev-server/sync-blocks-components.js';

interface BlocksCLIServerContextProps {
  enabled: boolean;
  port?: number;
}

const BlocksCLIServerContext =
  createContext<BlocksCLIServerContextProps | null>(null);

interface BlocksCLIDevServerProviderProps {
  httpServer: HttpServer | null;
  children: ReactNode;
}

export const BlocksCLIDevServerProvider: FC<
  BlocksCLIDevServerProviderProps
> = ({ httpServer, children }) => {
  const { t } = useTranslation();
  const { logger } = useDevEnvironment();
  const { model } = useProjectModel();
  const blocksComponentsRef = useBlocksComponentsRef();
  const { reportError } = useErrorReporter();

  const wixConfigRef = useRef(model.config);

  const [server] = useState<BlocksCLIServer | undefined>(() => {
    if (!model.config.blocks || !httpServer) {
      return;
    }

    const server = createBlocksCLIServer({
      httpServer,
      getInitParams: async () => ({
        revisionId: wixConfigRef.current.blocks?.uiRevision ?? '0',
      }),
      onSyncRevisionHandler: async ({ revisionId, widgets }) => {
        logger.info(t('dev_command.blocks.syncing_revision'));

        try {
          await syncBlocksComponents(
            model.projectFolder,
            widgets,
            blocksComponentsRef.current.widgets,
            blocksComponentsRef.current.panels
          );

          await saveWixConfig(model.projectFolder, {
            ...wixConfigRef.current,
            blocks: {
              ...(wixConfigRef.current.blocks ?? {}),
              uiRevision: revisionId,
            },
          });
        } catch (error) {
          reportError(error);
          logger.error(<ErrorViewer error={error} />);
          throw error;
        }
      },
    });

    return server;
  });

  useEffect(() => {
    wixConfigRef.current = model.config;
  }, [model.config]);

  useEffect(() => {
    if (!server) {
      return;
    }

    const removeOnConnectHandle = server.onConnect(() => {
      logger.info(t('dev_command.blocks_editor_connected'));
    });

    return () => {
      removeOnConnectHandle();
      server.close().catch(reportError);
    };
  }, [server, reportError, logger, t]);

  return (
    <BlocksCLIServerContext.Provider
      value={{
        enabled: !!server,
        port: server?.port,
      }}
    >
      {children}
    </BlocksCLIServerContext.Provider>
  );
};

export function useBlocksCLIDevServerProvider(): BlocksCLIServerContextProps {
  const context = useContext(BlocksCLIServerContext);
  if (context == null) {
    throw new Error(
      'useBlocksCLIDevServerProvider() cannot be used without an <BlocksCLIServerProvider>'
    );
  }
  return context;
}
